import { Component, computed, input } from '@angular/core';
import { DmAlert, DmIcon } from '@digitale-menschen/component-library-angular';

export type FormAlertVariant = 'error' | 'success';

/**
 * Form-level outcome message, rendered inside the form it belongs to rather than as a
 * toast in the page corner — feedback about a form has to sit next to the fields it
 * refers to, and one control must not report its success and its failure in two
 * different places.
 *
 * Renders nothing while `message` is empty. `color="error"` on the underlying `dm-alert`
 * is what earns `role="alert"` + `aria-live="assertive"`, so a failure appearing is
 * announced assertively while a success is announced politely; the caller clears the
 * message before a retry so the alert re-mounts and is announced again.
 *
 * Reserve this for outcomes the user stays on the form to see. A message that fires
 * while navigating away has no form left to attach to and belongs in a toast.
 */
@Component({
  selector: 'lp-form-alert',
  imports: [DmAlert, DmIcon],
  templateUrl: './form-alert.component.html',
  styleUrl: './form-alert.component.scss',
  host: {
    class: 'lp-form-alert',
  },
})
export class FormAlertComponent {
  public readonly message = input<string | null>(null);
  public readonly variant = input<FormAlertVariant>('error');
  /**
   * Overrides the variant's glyph, for a confirmation whose action is more telling than its
   * outcome — a resent code says more as a send icon than as another tick. The name must be
   * registered via `provideDmIcons` in every app that renders the form, since only 50 icons
   * ship by default.
   */
  public readonly icon = input<string | null>(null);
  protected readonly resolvedIcon = computed(() => this.icon() ?? this.variantIcons[this.variant()]);

  /** The glyph each variant carries unless {@link icon} overrides it. */
  private readonly variantIcons: Record<FormAlertVariant, string> = {
    error: 'alert-circle',
    success: 'check-circle',
  };
}
